import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Select, Statistic, Table, Tabs, Typography } from 'antd';
import {
  getSalesTeamAnalytics,
  getSalesTeamCommandCenter,
  listSalesTeams,
} from '@/services/salesTeam.service';

const { Title } = Typography;

const SalesTeamCommandCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const [teamId, setTeamId] = useState<string | undefined>();

  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(true),
  });

  const { data: center, isLoading } = useQuery({
    queryKey: ['sales-team-command-center', teamId],
    queryFn: () => getSalesTeamCommandCenter({ team_id: teamId }),
  });

  const { data: analytics } = useQuery({
    queryKey: ['sales-team-analytics', teamId],
    queryFn: () => getSalesTeamAnalytics({ team_id: teamId }),
  });

  const teamOptions = useMemo(
    () => [{ value: '', label: 'All visible teams' }, ...teams.map((t) => ({ value: t.id, label: t.team_name }))],
    [teams]
  );

  const pipelineColumns = [
    { title: 'Team', dataIndex: 'team_name', key: 'team_name' },
    { title: 'Open opps', dataIndex: 'open_opportunities', key: 'open_opportunities' },
    { title: 'Pipeline value', dataIndex: 'pipeline_value', key: 'pipeline_value' },
    { title: 'Open quotes', dataIndex: 'open_quotations', key: 'open_quotations' },
    { title: 'Draft SOs', dataIndex: 'draft_sales_orders', key: 'draft_sales_orders' },
  ];

  const funnelColumns = [
    { title: 'Stage', dataIndex: 'stage', key: 'stage' },
    { title: 'Count', dataIndex: 'cnt', key: 'cnt' },
    { title: 'Value', dataIndex: 'value_sum', key: 'value_sum' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Sales Team Command Center</Title>
      <Select
        allowClear
        style={{ width: 280, marginBottom: 16 }}
        placeholder="Filter by team"
        options={teamOptions}
        value={teamId ?? ''}
        onChange={(v) => setTeamId(v || undefined)}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Missing team docs" value={center?.exceptions?.missing_team_count ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading} hoverable onClick={() => navigate('/growth/follow-ups')}>
            <Statistic title="Overdue follow-ups" value={center?.exceptions?.overdue_follow_ups ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Stale opportunities" value={center?.exceptions?.stale_opportunities ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Open tasks" value={center?.work_queue?.open_tasks ?? 0} />
          </Card>
        </Col>
      </Row>

      <Tabs
        style={{ marginTop: 24 }}
        items={[
          {
            key: 'pipeline',
            label: 'Team summary',
            children: (
              <Table
                rowKey="team_id"
                loading={isLoading}
                dataSource={center?.teams ?? []}
                columns={pipelineColumns}
                pagination={false}
              />
            ),
          },
          {
            key: 'analytics',
            label: 'Analytics',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Statistic
                    title="Win conversion %"
                    value={analytics?.conversion_pct ?? 0}
                    suffix="%"
                  />
                </Card>
                <Table
                  rowKey="stage"
                  dataSource={analytics?.funnel ?? []}
                  columns={funnelColumns}
                  pagination={false}
                />
                {analytics?.team_comparison ? (
                  <Table
                    style={{ marginTop: 16 }}
                    rowKey="team_id"
                    dataSource={analytics.team_comparison}
                    columns={pipelineColumns}
                    pagination={false}
                  />
                ) : null}
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SalesTeamCommandCenterPage;
